import { Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { AtterbergGeneral, AtterbergDetail } from '../../../types/atterberg';
import { TrialsGrid } from './TrialsGrid';
import { ResultsSummary } from './ResultsSummary';
import { OverridePanel } from './OverridePanel';

interface Props {
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
  retestLLTrials: AtterbergDetail[];
  onGeneralChange: (field: keyof AtterbergGeneral, value: string | number | boolean | null) => void;
  onLLTrialsChange: (trials: AtterbergDetail[]) => void;
  onPLTrialsChange: (trials: AtterbergDetail[]) => void;
  onRetestLLTrialsChange: (trials: AtterbergDetail[]) => void;
  readOnly?: boolean;
}

export const AtterbergResultEntry = ({
  general,
  llTrials,
  plTrials,
  retestLLTrials,
  onGeneralChange,
  onLLTrialsChange,
  onPLTrialsChange,
  onRetestLLTrialsChange,
  readOnly = false,
}: Props) => {
  const theme = useTheme();
  const method = general.llMethod ?? 'Multipoint';
  const showRetest = !!general.organic;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Paper elevation={0} sx={{
        p: 1.25, borderRadius: 2,
        border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
      }}>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={!!general.isNonPlastic}
              onChange={(e) => onGeneralChange('isNonPlastic', e.target.checked)}
              disabled={readOnly}
            />
          )}
          label={<Typography sx={{ fontSize: '0.78rem' }}>Non-plastic</Typography>}
        />
      </Paper>
      <ResultsSummary
        general={general}
        llTrials={llTrials}
        plTrials={plTrials}
        retestLLTrials={retestLLTrials}
      />
      {/* Side-by-side LL (4 trials, wider) + PL (2 trials, narrower) per BUG-2026-0020.
          Stacks on narrow viewports. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 1,
        alignItems: 'flex-start',
      }}>
        <TrialsGrid
          title={`Liquid Limit — ${method === 'SinglePoint' ? 'Single-point' : 'Multipoint'}`}
          trials={llTrials}
          onChange={onLLTrialsChange}
          includeBlows
          method={method}
          showSinglePointLL={method === 'SinglePoint'}
          readOnly={readOnly}
        />
        <TrialsGrid
          title="Plastic Limit"
          trials={plTrials}
          onChange={onPLTrialsChange}
          includeBlows={false}
          method={method}
          showSinglePointLL={false}
          showAvgWaterContent
          readOnly={readOnly}
        />
      </Box>
      {/* FEAT-2026-0001: oven-dried retest LL pass — visible only when
          Organic is enabled. Same multipoint structure as the primary LL. */}
      {showRetest && (
        <TrialsGrid
          title={`Liquid Limit — Retest (oven-dried, ${method === 'SinglePoint' ? 'Single-point' : 'Multipoint'})`}
          trials={retestLLTrials}
          onChange={onRetestLLTrialsChange}
          includeBlows
          method={method}
          showSinglePointLL={method === 'SinglePoint'}
          readOnly={readOnly}
        />
      )}
      <OverridePanel data={general} onChange={onGeneralChange} readOnly={readOnly} />
    </Box>
  );
};
